import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Sparkles, ChevronDown } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { API_BASE_URL } from '../../config';
import { apiClient } from '../../services/api';

const RUTA = `${API_BASE_URL}/api/admin/asistente`;

/**
 * Asistente de datos del tablero.
 *
 * Pregunta en español, respuesta con cifras de la plataforma. Lo que **no**
 * hace, y es lo que lo vuelve confiable: el modelo no consulta la base. Elige
 * una métrica de un catálogo cerrado que calcula el servidor, y si la pregunta
 * no cae en ninguna dice que no lo sabe en vez de inventar un número.
 *
 * Por eso cada respuesta trae debajo, plegado, el dato crudo con el que se
 * armó: el administrador puede comprobar de dónde salió la cifra en vez de
 * tener que creerle.
 */
export default function AsistenteChat() {
  const [mensajes, setMensajes] = useState([]);
  const [pregunta, setPregunta] = useState('');
  const [pensando, setPensando] = useState(false);
  const [sugerencias, setSugerencias] = useState([]);
  const [modo, setModo] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const finRef = useRef(null);

  useEffect(() => {
    apiClient
      .get(`${RUTA}/estado`)
      .then((r) => {
        setSugerencias(r?.data?.sugerencias ?? []);
        setModo(r?.data?.modo ?? null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes, pensando]);

  const preguntar = async (texto) => {
    const q = (texto ?? pregunta).trim();
    if (!q || pensando) return;

    setPregunta('');
    const nuevos = [...mensajes, { rol: 'usuario', texto: q }];
    setMensajes(nuevos);
    setPensando(true);

    try {
      const r = await apiClient.post(RUTA, {
        pregunta: q,
        // Se manda solo lo último: el asistente responde cifras puntuales, no
        // necesita recordar media hora de conversación para eso.
        historial: nuevos.slice(-6, -1).map((m) => ({ rol: m.rol, texto: m.texto })),
      });
      setMensajes([...nuevos, { rol: 'asistente', ...r.data }]);
    } catch (e) {
      setMensajes([
        ...nuevos,
        { rol: 'asistente', texto: e.message || 'No pude responder. Intenta de nuevo.', error: true },
      ]);
    } finally {
      setPensando(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto flex flex-col" style={{ minHeight: 'calc(100vh - 8rem)' }}>
        <div className="mb-6">
          <h2 className="font-display text-vd-h2 text-vd-tinta mb-2 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-vd-naranja" />
            Asistente de datos
          </h2>
          <p className="text-vd-pizarra">
            Pregúntale por las cifras de vondi en español. Las calcula del tablero, no las inventa.
          </p>
          {modo === 'sin_modelo' && (
            <p className="text-xs text-vd-ambar mt-2">
              Funcionando sin modelo de lenguaje: entiende preguntas sencillas por palabras clave. Los números
              son los mismos.
            </p>
          )}
        </div>

        {/* Conversación */}
        <div className="flex-1 space-y-4 mb-4">
          {mensajes.length === 0 && (
            <div className="bg-white border border-vd-linea rounded-card p-6">
              <p className="text-vd-tinta font-medium mb-3">Prueba con algo así:</p>
              <div className="flex flex-wrap gap-2">
                {sugerencias.map((s) => (
                  <button
                    key={s}
                    onClick={() => preguntar(s)}
                    className="px-4 py-2 rounded-full border border-vd-linea bg-vd-niebla text-sm text-vd-grafito hover:border-vd-naranja hover:text-vd-naranja transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mensajes.map((m, i) =>
            m.rol === 'usuario' ? (
              <div key={i} className="flex justify-end">
                <div className="bg-grad-vivo text-white rounded-card rounded-br-md px-5 py-3 max-w-[80%] shadow-warm-sm">
                  {m.texto}
                </div>
              </div>
            ) : (
              <div key={i} className="flex justify-start">
                <div
                  className={`rounded-card rounded-bl-md px-5 py-4 max-w-[85%] border ${
                    m.error ? 'bg-vd-alerta/8 border-vd-alerta/25' : 'bg-white border-vd-linea shadow-warm-sm'
                  }`}
                >
                  <p className="text-vd-tinta whitespace-pre-line leading-relaxed">{m.texto}</p>

                  {/* De dónde salió la cifra. Plegado, pero disponible. */}
                  {m.datos && (
                    <div className="mt-3 pt-3 border-t border-vd-linea">
                      <button
                        onClick={() => setAbierto(abierto === i ? null : i)}
                        className="text-xs text-vd-pizarra hover:text-vd-tinta flex items-center gap-1"
                      >
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${abierto === i ? 'rotate-180' : ''}`}
                        />
                        De dónde sale este número
                        {m.metrica && <span className="text-vd-pizarra/70">· {m.metrica}</span>}
                      </button>
                      {abierto === i && (
                        <pre className="mt-2 text-[11px] text-vd-pizarra bg-vd-niebla rounded-input p-3 overflow-x-auto">
                          {JSON.stringify(m.datos, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {pensando && (
            <div className="flex justify-start">
              <div className="bg-white border border-vd-linea rounded-card px-5 py-4 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-vd-naranja" />
                <span className="text-vd-pizarra text-sm">Consultando...</span>
              </div>
            </div>
          )}
          <div ref={finRef} />
        </div>

        {/* Entrada */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            preguntar();
          }}
          className="sticky bottom-0 bg-white border border-vd-linea rounded-full shadow-warm p-2 flex items-center gap-2"
        >
          <Input
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            placeholder="¿Cuánto se ha ahorrado la gente este mes?"
            className="flex-1 border-0 bg-transparent h-11 px-4 focus-visible:ring-0 text-vd-tinta"
            disabled={pensando}
          />
          <Button
            type="submit"
            variant="vondi"
            size="icon"
            className="rounded-full w-11 h-11 shrink-0"
            disabled={!pregunta.trim() || pensando}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
